import React, {useEffect} from 'react';
import {
	DatePicker,
	DateRangePicker,
	DateRangePickerProps,
	FormField,
	Input,
	Multiselect,
	RadioGroup,
	Select,
	Spinner,
	Textarea,
	TimeInput,
} from '@amzn/awsui-components-console';
import {useController, useFormContext, useWatch} from 'react-hook-form';
import {DataType} from '@amzn/snow-nxt-workflow-service-typescript-client';
import {SNOWFormAttributeProps} from './SNOWFormAttribute.interface';
import MultiInputHtmlElement from '../multiInputHtmlElement/multiInputHtmlElement';
import {AUTOPOPULATION_OPTIONS_FIELD_PREFIX, UNICODE_REGEX} from 'src/constants/CommonConstants';
import {convertStringListToOptions, convertStringToArray,} from '../taskInputs/taskInputsUtils';
import './SNOWFormAttribute.scss';

export default function SNOWFormAttribute({
	attributeId,
	attributeName,
	description,
	disabled,
	helpText,
	dataType,
	placeholder,
	mandatory,
	regex,
	options,
	prefetchedValue,
	handleAutoPopulation,
	isAutopopulating = false,
	isOverridable = true,
	persistAsString = false,
}: SNOWFormAttributeProps) {
	const { control, trigger, setValue } = useFormContext();
	const fieldRegex = regex ? regex : UNICODE_REGEX;

	const getIsAttributeDisabled = (currentValue: any): boolean => {
		if (disabled) {
			return true;
		}

		if (isOverridable) {
			return false;
		}

		return prefetchedValue != null || isAutopopulating ||
			(currentValue != null && currentValue !== '' && currentValue !== false && currentValue !== 0);
	};
	
	const {
		field,
		fieldState: { error },
		formState: { errors, isValid },
	} = useController({
		name: attributeId!,
		control,
		rules: {
			required: { value: mandatory, message: 'This is a required field' },
			pattern: {
				value: new RegExp(fieldRegex),
				//TODO: Add Error Text to SNOWAttribute Smithy
				message:
					helpText || `Regex Validation failed. Must be of form ${fieldRegex}`,
			},
		},
	});

	const hasExceptionOrError = field.value && typeof field.value === 'string' && 
		(field.value.toLowerCase().includes('exception') || field.value.toLowerCase().includes('error'));

	const runtimeOptions = useWatch({ control, name: `${AUTOPOPULATION_OPTIONS_FIELD_PREFIX}.${attributeId}` }) as string[] | undefined;
	const effectiveOptions = runtimeOptions?.length ? runtimeOptions : options;

	const handleChange = (newValue: any, onChange: (value: any) => void) => {
		onChange(newValue);
		if (handleAutoPopulation && attributeId) {
			handleAutoPopulation(attributeId);
		}
		if (Object.keys(errors).length !== 0 && !isValid) {
			trigger(attributeId);
		}
	};

	useEffect(() => {
		if (prefetchedValue != null) {
			const prefetchedHasError = prefetchedValue &&
				(prefetchedValue.toLowerCase().includes('exception') || prefetchedValue.toLowerCase().includes('error'));
			if (!prefetchedHasError) {
				setValue(attributeId!, prefetchedValue);
			}
		}
	}, [prefetchedValue, attributeId, setValue]);

	const renderSelectedComponent = ({ onChange, value, ref }) => {
		let inputDiv;
		const inputDataTestId = `requirements-input-${attributeId
			?.split('.')
			.join('-')}`;
		const displayValue = hasExceptionOrError ? '' : value;
		const isAttributeDisabled = getIsAttributeDisabled(value);

		switch (dataType) {
			case DataType.DATE_TIME:
				inputDiv = (
					<div className="dateTimePicker">
						<DatePicker
							onChange={({ detail }) => {
								const timePart = value?.includes('T')
									? value.split('T')[1]
									: '00:00:00.000T';
								const concatValue = detail.value + 'T' + timePart;

								return onChange(concatValue);
							}}
							value={(() => {
								return value?.includes('T') ? value.split('T')[0] : '';
							})()}
							isDateEnabled={(selectedDate) => {
								if (!(selectedDate instanceof Date)) {
									return false;
								}

								const today = new Date();

								today.setHours(0, 0, 0, 0);

								return selectedDate >= today;
							}}
							disabled={isAttributeDisabled}
							placeholder="YYYY/MM/DD"
						/>
						<TimeInput
							onChange={({ detail }) => {
								const datePart = value?.includes('T')
									? value.split('T')[0]
									: new Date().toISOString().split('T')[0];

								return onChange(`${datePart}T${detail.value}.000Z`);
							}}
							value={(() => {
								let timePart = '';

								if (value?.includes('T') && value?.includes('.')) {
									timePart = value.split('T')[1].split('.')[0];
								}

								return timePart;
							})()}
							disabled={isAttributeDisabled}
							placeholder="hh:mm:ss"
						/>
					</div>
				);
				break;
			case DataType.TIME:
				inputDiv = (
					<TimeInput
						onChange={({ detail }) => onChange(detail.value)}
						value={displayValue}
						data-testid={inputDataTestId}
						ref={ref}
						disabled={isAttributeDisabled}
						placeholder="hh:mm:ss"
					/>
				);
				break;
			case DataType.DATE:
				inputDiv = (
					<DatePicker
						onChange={({ detail }) => onChange(detail.value)}
						value={displayValue}
						disabled={isAttributeDisabled}
						placeholder="YYYY/MM/DD"
					/>
				);
				break;
			case DataType.DATE_RANGE:
				inputDiv = inputDiv = (
					<DateRangePicker
						disabled={isAttributeDisabled}
						onChange={({ detail }) => {
							return onChange(JSON.stringify(detail.value));
						}}
						value={(() => {
							try {
								const parsedValue = JSON.parse(value as string);

								return {
									startDate: parsedValue?.startDate || null,
									endDate: parsedValue?.endDate || null,
									type: 'absolute',
								} as DateRangePickerProps.Value;
							} catch (error) {
								return {
									startDate: '',
									endDate: '',
									type: 'absolute',
								} as DateRangePickerProps.Value;
							}
						})()}
						isValidRange={(range) => {
							if (range?.type === 'absolute') {
								const [startDateWithoutTime] = range.startDate.split('T');
								const [endDateWithoutTime] = range.endDate.split('T');

								if (!startDateWithoutTime || !endDateWithoutTime) {
									return {
										valid: false,
										errorMessage:
											'The selected date range is incomplete. Select a start and end date for the date range.',
									};
								}

								if (new Date(range.startDate) > new Date(range.endDate)) {
									return {
										valid: false,
										errorMessage:
											'The selected date range is invalid. The start date must be before the end date.',
									};
								}
							}

							return { valid: true };
						}}
						i18nStrings={{
							applyButtonLabel: 'Apply',
							cancelButtonLabel: 'Cancel',
							clearButtonLabel: 'Clear and Dismiss',
						}}
						placeholder="Select the date and time range"
						rangeSelectorMode="absolute-only"
						relativeOptions={[]}
					/>
				);
				break;
			case DataType.NUMBER:
				inputDiv = (
					<Input
						type="number"
						value={displayValue}
						placeholder={placeholder}
						disabled={isAttributeDisabled}
						onChange={({ detail }) => {
							const numericValue =
								detail.value === '' ? 0 : parseInt(detail.value, 10) || 0;
							handleChange(
								persistAsString ? String(numericValue) : numericValue,
								onChange
							);
						}}
						ref={ref}
						data-testid={inputDataTestId}
						inputMode="numeric"
					/>
				);
				break;
			case DataType.PARAGRAPH:
				inputDiv = (
					<Textarea
						value={displayValue}
						placeholder={placeholder}
						disabled={isAttributeDisabled}
						onChange={({ detail }) => handleChange(detail.value, onChange)}
						ref={ref}
						data-testid={inputDataTestId}
						rows={10}
					/>
				);
				break;
			case DataType.BOOLEAN:
				inputDiv = (
					<RadioGroup
						onChange={({ detail }) => handleChange(detail.value, onChange)}
						value={displayValue}
						items={[
							{ value: 'true', label: 'Yes' },
							{ value: 'false', label: 'No' },
						]}
						data-testid={inputDataTestId}
					/>
				);
				break;
			case DataType.LIST:
				inputDiv = effectiveOptions ? (
					<Multiselect
						disabled={isAttributeDisabled}
						selectedOptions={hasExceptionOrError ? [] : convertStringToArray(value as string)?.map(
							(listItem: string) => ({ label: listItem, value: listItem })
						)}
						onChange={({ detail }) => {
							const newSelectedOptions = detail.selectedOptions.map(
								({ value }) => value
							);
							handleChange(newSelectedOptions.toString(), onChange);
						}}
						options={convertStringListToOptions(effectiveOptions)}
						placeholder="Choose options"
						data-testid={inputDataTestId}
					/>
				) : (
					<>
						<MultiInputHtmlElement
							tokens={hasExceptionOrError ? [] : convertStringToArray(value as string)?.map((item) => ({
								label: item,
								dismissLabel: `Remove Item ${item}`,
							}))}
							onAdd={(input) => {
								const newValue = [
									...convertStringToArray(value as string),
									input,
								];

								onChange(newValue.toString());
							}}
							onRemove={({ detail: { itemIndex } }) => {
								const tokens = convertStringToArray(value);
								const newTokens = [
									...tokens.slice(0, itemIndex),
									...tokens.slice(itemIndex + 1),
								];

								onChange(newTokens.toString());
							}}
							dataTestId={inputDataTestId}
						/>
					</>
				);
				break;
			case DataType.STRING:
			default:
				inputDiv = effectiveOptions ? (
					<Select
					disabled={isAttributeDisabled}
						selectedOption={hasExceptionOrError ? null : { label: value as string, value: value as string }}
						onChange={({ detail }) => handleChange(detail.selectedOption.value, onChange)}
						options={convertStringListToOptions(effectiveOptions)}
						data-testid={inputDataTestId}
					/>
				) : (
					<Input
						type="text"
						value={displayValue}
						placeholder={placeholder}
						disabled={isAttributeDisabled}
						onChange={({ detail }) => {
							handleChange(detail.value, onChange);
							if (
								prefetchedValue != null ||
								(Object.keys(errors).length !== 0 && !isValid)
							)
								trigger(attributeId);
						}}
						ref={ref}
						data-testid={inputDataTestId}
					/>
				);
				break;
		}

		return inputDiv;
	};

	const fieldDataTestId = `requirements-field-${attributeId
		?.split('.')
		.join('-')}`;

	return (
		<FormField
			label={
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					{attributeName} {!mandatory && '- optional'}
					{isAutopopulating && <Spinner size={"normal"} />}
				</div>
			}
			description={description}
			constraintText={helpText}
			errorText={error?.message}
			data-testid={fieldDataTestId}
		>
			{renderSelectedComponent(field)}
		</FormField>
	);
}
